// Route-based code-splitting helper (canonical home — consumed by the
// workspace shell AND sub-app packages like analytics, so every package's
// page registry can defer its page modules to dynamic-import chunks).
//
// Wraps a heavy sub-app page module in a lightweight placeholder so its code
// lands in a separate dynamic `import()` chunk instead of the initial shell
// bundle. Today `workspace/context.js` → `pages/index.js` (and `pages/main.js`'s
// `_APP_WINDOW_PAGES`) STATICALLY import every sub-app page, so loading ANY
// workspace route — including `/` — pulls the entire sub-app page payload
// (~800K of source; canvas pages alone are 528K) into the context chunk. That
// is the dominant driver of the ~5.8s cold FCP.
//
// The placeholder registers the route eagerly (so smbls's auto-router and the
// home AppWindow PageSlot can mount it synchronously), then on create it
// imports the real page module and swaps it in via `el.set` — the same
// imperative-mount pattern the home AgentsWidget canvas uses (verified: a
// ~500-node `el.set` mount is ~741ms and does NOT freeze the renderer).
//
// Used IDENTICALLY by:
//   • the auto-router (a `context.pages['/route']` value), and
//   • the home AppWindow PageSlot (`_APP_WINDOW_PAGES['/route']`),
// since both just `el.set()` the value — the placeholder self-loads either way.
//
// Usage:
//   '/chat': lazyPage(
//     () => import('@symbo-ls/workspace-chat/pages/index.js').then(m => m.home),
//     { title: 'Chat / Symbols Workspace' }
//   )
//
// The loader MUST resolve to the page object (do the `.then(m => m.export)` in
// the loader) so a single dynamic import per sub-app package yields one async
// chunk shared by all of that package's routes.
// NOTE on lifecycle: `el.set(page)` routes through `context.__create`
// (element/src/methods.js set → create.js), which fires the loaded page's
// own onCreate/onRender on the mounted content element. Do NOT invoke the
// page's lifecycle manually here — that double-runs non-idempotent onCreate
// work (presence subscriptions, board loaders) against the wrapper element,
// which has no onRemove to clean them up.
// Add-missing fold of a shared-library context ({ components, functions,
// state? }) into the LIVE app registry — the single mechanism behind the
// lazySharedLib / lazySharedLibPage route folds AND imperative folds (the
// messenger's ensureChatContext). Never clobbers an existing key: the eager
// slice / an earlier fold wins, so double-folding is a no-op by design.
export const foldSharedLibContext = (root, lib) => {
  const ctx = root.context
  if (lib?.components && ctx?.components) {
    for (const k in lib.components) {
      if (!ctx.components[k]) ctx.components[k] = lib.components[k]
    }
  }
  if (lib?.functions && ctx?.functions) {
    for (const k in lib.functions) {
      if (!ctx.functions[k]) ctx.functions[k] = lib.functions[k]
    }
  }
  if (lib?.state) {
    const seed = {}
    for (const k in lib.state) if (root[k] === undefined) seed[k] = lib.state[k]
    if (Object.keys(seed).length) root.update?.(seed)
  }
}

export const lazyPage = (loader, metadata) => ({
  height: '100%',
  minHeight: '100%',
  // Carry the title so @symbo.ls/helmet sets the tab title immediately, before
  // the chunk resolves; the real page's own `metadata` re-applies on mount.
  metadata,
  // The loader rides in `scope`, never a closure over the factory argument: a
  // published library serializes this definition, and a closure's free
  // variable is gone there (frank FA205) — the route stayed on its
  // placeholder. Same shape as the shell's own pages/lazyPage.js.
  scope: { loader },
  onCreate: async (el) => {
    if (el.__lazyLoaded) return
    el.__lazyLoaded = true
    // Late-resolve guard — the chunk import is async, so the router (or the
    // AppWindow PageSlot) can navigate away while it's in flight. The
    // placeholder's node is then detached, and el.set on a detached element
    // REVIVES the dead page as a second router content node at the shell root
    // — observed as the /admin page rendering below home after the demo
    // route guard bounced /admin → / before the admin chunk resolved (the
    // shell grid split into two ~half-viewport rows, and the dead page's
    // helmet re-wrote the tab title). Skipping the swap when the node is no
    // longer in the document closes the race for both mount paths.
    // "Gone" means DISPOSED — dispose() clears el.__ref (and el.node) — not
    // "not connected YET": smbls 3.14.84x builds an app's whole first view
    // detached and attaches it in one insertion (firstRender), so a loader
    // that settles before that insertion is NOT a navigation away. The old
    // `!el.node.isConnected` guard read it as gone and left the route on its
    // skeleton forever (the shell's signed-out /login, 2026-10-21, fixed
    // the same way in workspace af8b61d14). el.whenConnected runs the swap
    // now when connected, else in the task that inserts the node, and never
    // for a disposed element.
    const _place = (def) => {
      if (!el.__ref) return
      if (typeof el.whenConnected === 'function') el.whenConnected(() => el.set(def))
      else el.set(def)
    }
    try {
      const page = await el.scope.loader()
      if (!page) throw new Error('loader resolved empty')
      _place({ ...page, height: '100%', minHeight: '100%' })
    } catch (err) {
      console.error('[lazyPage] failed to load route module:', err && err.message)
      _place({
        tag: 'div',
        padding: 'B',
        color: 'caption',
        text: '{{ shell.errorState.lazyPage | polyglot }}'
      })
    }
  }
})

// Lazy SHARED-LIBRARY loader — where `lazyPage` defers a single page module,
// `lazySharedLib` defers an ENTIRE self-contained shared library (its
// components + functions, and any seed state) out of the eager shell bundle.
//
// A shared library listed in `sharedLibraries.js` is otherwise instantiated on
// EVERY route (smbls's `mergeSharedLibraries` folds its components/functions/
// pages into the shell context at boot). For a hard-leaf library — one that no
// other package string-extends, bare-keys, or `el.call`s — that eager cost is
// pure waste until the user actually visits one of its routes. This wrapper
// moves that library into a dynamic-import chunk and registers it lazily.
//
// Mechanism (the proven one from canvas/functions/injectPageInApp.js): grab the
// live merged registry off the router root (`root.context`), copy the library's
// components + functions into it with add-missing semantics (identical to
// smbls's `mergeOneLibrary`), seed any state it ships, THEN `el.set()` the
// picked page. Bare-key children (`InfraSidebar`, `ServiceNode`, …) and
// `el.call('<fn>')` inside the mounted page resolve against the now-populated
// `context.components` / `context.functions` on that next mount — smbls's
// `call()` reads `this.context.functions[name]` live, and bare keys resolve
// against `context.components` at create time, exactly as injectPageInApp relies
// on. Pages are NOT boot-merged here; each route is registered directly as a
// `lazySharedLib(...)` entry in the shell's page map, and `pickPage` selects
// which of the library's `pages` to render.
//
// Usage (workspace/pages/index.js):
//   '/infra': lazySharedLib(
//     () => import('@symbo-ls/workspace-infra'),
//     (lib) => lib.pages['/infra'],
//     { title: 'Infra / Symbols Workspace' }
//   )
//
// `ctxLoader` resolves to the library context (or a module whose `.default` is
// it — context.js default export `{ components, functions, state?, pages }`).
// The detached-node guard matches `lazyPage`: the async import can resolve after
// the router (or the PageSlot) navigated away, so skip the swap when the
// placeholder's node has left the document.
export const lazySharedLib = (ctxLoader, pickPage, metadata) => ({
  height: '100%',
  minHeight: '100%',
  metadata,
  scope: { ctxLoader, pickPage },
  onCreate: async (el) => {
    if (el.__lazyLibLoaded) return
    el.__lazyLibLoaded = true
    // The late-resolve guard of lazyPage above (disposed → never; not
    // attached yet → when it is).
    const _place = (def) => {
      if (!el.__ref) return
      if (typeof el.whenConnected === 'function') el.whenConnected(() => el.set(def))
      else el.set(def)
    }
    try {
      const mod = await el.scope.ctxLoader()
      // context.js default export: { components, functions, state?, pages }
      const lib = mod && mod.default ? mod.default : mod
      // A shared-lib context that resolves with NO components AND NO functions
      // is a broken/stale bundle — its `import * as` namespaces came across
      // empty (default imports like `pages`/`state` can still be fine). The
      // add-missing merges below would then silently no-op and the mounted
      // page's bare keys would render an empty shell (the observed blank
      // Marketplace, 2026-07-21). Fail into the catch — which resets the retry
      // guard + shows a Refresh prompt — instead of a silent blank page.
      if (!lib || (!lib.components && !lib.functions)) {
        throw new Error(
          'shared-lib context resolved with no components/functions — stale/mismatched bundle (rebuild needed)'
        )
      }
      const root = el.getRoot?.() || el
      foldSharedLibContext(root, lib)
      const page = el.scope.pickPage(lib)
      if (!page) throw new Error('shared-lib page resolved empty')
      _place({ ...page, height: '100%', minHeight: '100%' })
    } catch (err) {
      // Reset the load guard so a transient/stale failure (a flapping dev
      // server, or a bundle that hasn't rebuilt yet) can retry on the next
      // mount instead of leaving the route permanently dead. (The guard is set
      // eagerly above to prevent re-entrancy during the in-flight import.)
      el.__lazyLibLoaded = false
      console.error('[lazySharedLib] load failed:', err && err.message)
      _place({
        tag: 'div',
        role: 'alert',
        padding: 'B',
        color: 'caption',
        text: '{{ shell.errorState.lazyPage | polyglot }}'
      })
    }
  }
})

// Lazy SHARED-LIBRARY + PAGE loader — the variant for a deferred library whose
// context.js ships components + functions + state but has NO `.pages` map (so the
// route's page module lives in a SEPARATE file, e.g. `@symbo-ls/workspace-documents`
// whose default export is `{ components, functions, state }` and whose pages are
// `@symbo-ls/workspace-documents/pages/*.js`). Where `lazySharedLib` reads the page
// off `lib.pages[...]`, this takes an explicit second loader for the page module.
//
// Mechanism is identical to `lazySharedLib`: on first create it dynamic-imports the
// library CONTEXT and folds its components + functions into the LIVE `root.context`
// registry with add-missing semantics (the proven injectPageInApp pattern), seeds
// any missing state keys, THEN dynamic-imports the PAGE module and `el.set`s it. Both
// imports are module-cached after first use (the browser/bundler dedupes the dynamic
// import), and the `__lazyLibPageLoaded` guard keeps a single element from re-running.
// Bare-key children in the mounted page (`DocsMain`, `NotesMain`, …) and any
// `el.call('<fn>')` resolve against the now-populated `context.components` /
// `context.functions` on that next mount — smbls's `call()` reads
// `this.context.functions[name]` live, and bare keys resolve against
// `context.components` at create time.
//
// The eagerly-carved slice of such a library (components/functions referenced from
// OUTSIDE the library on other routes — e.g. documents' `DocsSidebar` + `createNote`)
// must be registered separately via `sharedLibraries.js`; this loader only covers the
// route-local remainder. Add-missing means the eager slice is never double-registered.
//
// Usage (workspace/pages/index.js):
//   '/notes': lazySharedLibPage(
//     () => import('@symbo-ls/workspace-documents'),
//     () => import('@symbo-ls/workspace-documents/pages/index.js').then((m) => m.home),
//     { title: 'Notes / Symbols Workspace' }
//   )
//
// `ctxLoader` resolves to the library context (or a module whose `.default` is it).
// `pageLoader` resolves to the page object (do the `.then((m) => m.export)` in the
// loader). The detached-node guard matches `lazyPage` / `lazySharedLib`.
export const lazySharedLibPage = (ctxLoader, pageLoader, metadata) => ({
  height: '100%',
  minHeight: '100%',
  metadata,
  scope: { ctxLoader, pageLoader },
  onCreate: async (el) => {
    if (el.__lazyLibPageLoaded) return
    el.__lazyLibPageLoaded = true
    // The late-resolve guard of lazyPage above (disposed → never; not
    // attached yet → when it is).
    const _place = (def) => {
      if (!el.__ref) return
      if (typeof el.whenConnected === 'function') el.whenConnected(() => el.set(def))
      else el.set(def)
    }
    try {
      const mod = await el.scope.ctxLoader()
      // context.js default export: { components, functions, state? }
      const lib = mod && mod.default ? mod.default : mod
      const root = el.getRoot?.() || el
      foldSharedLibContext(root, lib)
      const page = await el.scope.pageLoader()
      if (!page) throw new Error('page loader resolved empty')
      _place({ ...page, height: '100%', minHeight: '100%' })
    } catch (err) {
      console.error('[lazySharedLibPage] load failed:', err && err.message)
      _place({
        tag: 'div',
        padding: 'B',
        color: 'caption',
        text: '{{ shell.errorState.lazyPage | polyglot }}'
      })
    }
  }
})
